import { WorkflowTraceSchema } from '@toolproof-core/genesis/zod/workflow-trace';

import { selectEffectiveConfig } from '@/workflow/modes/_@shared/_pure/configSelection';
import { isWorkflowTrace } from '@/workflow-runner/_pure/model/traceHelpers';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';

export async function loadLocalTraces(config: Config, signal: AbortSignal): Promise<Partial<CosmosDataModel>> {
    const runnerConfig = selectEffectiveConfig(config, 'runner');
    if (runnerConfig.adapterMode !== 'runner') throw new Error('Expected Runner config when selecting a trace source.');
    const source = runnerConfig.adapterModeConfig.traceSource ?? 'remote';
    if (source === 'remote') return {};
    if (source !== 'local') throw new Error(`Unknown Runner trace source: ${source}`);
    if (process.env.NODE_ENV === 'production') {
        return { localWorkflowTraces: { traces: [], timestampByHandle: {} } };
    }
    const response = await fetch('/api/local-workflow-traces', { signal, cache: 'no-store' });
    const payload: unknown = await response.json();
    if (!response.ok) {
        const message = payload && typeof payload === 'object' && 'error' in payload ? String(payload.error) : response.statusText;
        throw new Error(`Could not load local workflow traces: ${message}`);
    }
    if (!payload || typeof payload !== 'object' || !('traces' in payload) || !Array.isArray(payload.traces)
        || !('timestampByHandle' in payload) || !payload.timestampByHandle || typeof payload.timestampByHandle !== 'object') {
        throw new Error('Invalid local workflow trace catalog.');
    }
    const traces = payload.traces.map((payload: unknown) => {
        const trace = WorkflowTraceSchema.parse(payload);
        if (!isWorkflowTrace(trace)) throw new Error('Invalid local WorkflowTrace payload.');
        return trace;
    });
    const timestampByHandle: Record<string, string> = {};
    for (const trace of traces) {
        if (Object.hasOwn(timestampByHandle, trace.handle)) throw new Error(`Duplicate local WorkflowTrace handle '${trace.handle}'.`);
        const timestamp: unknown = Reflect.get(payload.timestampByHandle, trace.handle);
        if (typeof timestamp !== 'string' || !Number.isFinite(Date.parse(timestamp))) {
            throw new Error(`Invalid local trace timestamp for '${trace.handle}'.`);
        }
        timestampByHandle[trace.handle] = timestamp;
    }
    return { localWorkflowTraces: { traces, timestampByHandle } };
}
